import React, { useEffect } from 'react';

import { VerificationCodeStatus } from 'api/curo/VerificationProcessApi';
import FormTitle from 'components/PageLayout/FormTitle';
import LoaderOverlay from 'components/PageLayout/LoaderOverlay';
import AuthCodeInput from 'components/curo-components/common/AuthCodeInput';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { getEmailVerificationData, getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { useLoadingState } from 'hooks/useLoadingState';
import styles from 'pages/CuroPages/ApplicationProcess/steps/VerificationProcessStep/VerificationProcessStep.module.scss';
import { IDefaultStepProps } from 'pages/CuroPages/types';
import { sendEmailVerificationCode, verifyEmailVerificationCode } from 'thunks';

interface IEmailVerificationStepProps extends IDefaultStepProps {
  onStepFailed: () => void;
}

const PAGE_TITLE = 'Email Verification';

const EmailVerificationStep = ({ onStepComplete, onStepFailed }: IEmailVerificationStepProps) => {
  const emailVerificationData = useAppSelector(getEmailVerificationData);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const trackEvent = useGoogleAnalytics();

  const [isPageLoading, setIsPageLoading] = useLoadingState();
  const { email, accountData } = useAppSelector(getSignInProcessState);

  const handleSendEmailVerificationCode = async () => {
    if (accountData?.currentApplicationId) {
      if (isPageLoading === 'idle') {
        setIsPageLoading('loading');
      }

      const { status } = await dispatchWithUnwrap(
        sendEmailVerificationCode(accountData.currentApplicationId),
      );

      setIsPageLoading('success');

      if (status === VerificationCodeStatus.Canceled) {
        onStepFailed();
        return;
      }
    }
  };

  useEffect(() => {
    handleSendEmailVerificationCode();
    trackEvent(GoogleAnalyticsEventType.ApplyProcessEmailVerify);
  }, []);

  const handleVerifyCode = async (code: string) => {
    if (accountData?.currentApplicationId) {
      const { status } = await dispatchWithUnwrap(
        verifyEmailVerificationCode({
          applicationId: accountData.currentApplicationId,
          code,
        }),
      );

      if (status === VerificationCodeStatus.Canceled) {
        onStepFailed();
        return;
      }

      if (status === VerificationCodeStatus.Approved) {
        onStepComplete();
        return;
      }
    }
  };

  if (isPageLoading === 'loading' || isPageLoading === 'idle' || !emailVerificationData) {
    return <LoaderOverlay />;
  }

  const { sendingAttemptsLeft } = emailVerificationData;

  return (
    <>
      <FormTitle
        title={PAGE_TITLE}
        subTitle={
          <p className={styles.stepDescription}>
            <span>
              We have sent a 6-digit verification code to <strong>{email}</strong>.
            </span>
            <span>
              Please check your email and enter the code below. The code will be valid for 10
              minutes.
            </span>
            <br />
            <strong>If you do not receive a code, please check your junk/spam folder.</strong>
          </p>
        }
      />
      <AuthCodeInput
        buttonTitle="Continue"
        handleResendCode={handleSendEmailVerificationCode}
        handleVerifyCode={handleVerifyCode}
        sendingAttemptsLeft={sendingAttemptsLeft}
      />
    </>
  );
};

export default EmailVerificationStep;
